import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../lib/toast';
import { formatRelative } from '../../lib/format';
import type { Organization } from '@shared/types';

interface ListResult {
  items: Organization[];
  total: number;
}

export function AdminOrgsPage() {
  const [data, setData] = useState<ListResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function load() {
    try {
      setData(await api.get<ListResult>('/api/admin/orgs?pageSize=200'));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/admin/orgs', { name, slug });
      toast.success('Organization created');
      setName('');
      setSlug('');
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Organizations</h1>
        <p className="text-sm text-ink-500 dark:text-ink-400">
          Manage tenants on the platform.
        </p>
      </header>

      <form onSubmit={onCreate} className="card p-5 grid gap-3 md:grid-cols-3 items-end">
        <div>
          <label className="label">Name</label>
          <input
            required
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Slug (optional)</label>
          <input
            className="input"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
        </div>
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving && <Spinner size={16} />} Create
        </button>
      </form>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 px-4 py-3 text-sm">
          {error}
        </div>
      )}
      {!data && (
        <div className="flex items-center justify-center py-16">
          <Spinner size={28} />
        </div>
      )}
      {data && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Plan</th>
                <th>Created</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((o) => (
                <tr key={o.id}>
                  <td className="font-medium">{o.name}</td>
                  <td className="font-mono text-xs">{o.slug}</td>
                  <td>
                    <span className="badge-info">{o.plan}</span>
                  </td>
                  <td className="text-xs text-ink-500 dark:text-ink-400">
                    {formatRelative(o.createdAt)}
                  </td>
                  <td>
                    {o.disabledAt ? (
                      <span className="badge-danger">disabled</span>
                    ) : (
                      <span className="badge-success">active</span>
                    )}
                  </td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={async () => {
                        try {
                          await api.patch(`/api/admin/orgs/${o.id}`, {
                            disabled: !o.disabledAt,
                          });
                          await load();
                        } catch (err) {
                          toast.error(err instanceof Error ? err.message : 'Failed');
                        }
                      }}
                    >
                      {o.disabledAt ? 'Enable' : 'Disable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
